import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, message } from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { vppResources } from '../../../services/api';

export default function VppResources() {
  const [loading, setLoading] = useState(true);
  const [aggregate, setAggregate] = useState(null);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    Promise.all([vppResources.aggregate(), vppResources.list()])
      .then(([aggRes, listRes]) => {
        setAggregate(aggRes.data);
        setResources((listRes.data || []).map(r => ({
          key: r.id,
          name: r.name,
          type: r.type,
          capacity: parseFloat(r.rated_capacity),
          available: parseFloat(r.available_capacity),
          up: parseFloat(r.regulation_up_capacity || 0),
          down: parseFloat(r.regulation_down_capacity || 0),
          response: r.response_time_ms ?? null,
          regulation_rate: r.regulation_rate ?? null,
          status: r.registration_status,
        })));
      })
      .catch(err => message.error(err.message || '加载VPP资源失败'))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { title: '资源名称', dataIndex: 'name' },
    { title: '类型', dataIndex: 'type', render: t => <Tag color={t === '光伏' ? 'green' : t === '储能' ? 'blue' : t === '充电桩' ? 'purple' : 'orange'}>{t}</Tag> },
    { title: '额定容量(kW)', dataIndex: 'capacity', sorter: (a, b) => a.capacity - b.capacity },
    { title: '可用容量(kW)', dataIndex: 'available' },
    { title: '可上调(kW)', dataIndex: 'up', render: v => <span style={{ color: '#faad14' }}>{v}</span> },
    { title: '可下调(kW)', dataIndex: 'down', render: v => <span style={{ color: '#52c41a' }}>{v}</span> },
    { title: '响应时间(ms)', dataIndex: 'response', render: v => v ?? '—' },
    { title: '注册状态', dataIndex: 'status', render: v => <Tag color={v === '已注册' ? 'green' : 'default'}>{v}</Tag> },
  ];

  const chartOption = aggregate ? {
    tooltip: { trigger: 'item', formatter: '{b}: {c} kW ({d}%)' },
    series: [{
      type: 'pie', radius: ['40%', '70%'],
      data: Object.entries(aggregate.by_type || {}).map(([name, info]) => ({
        name,
        value: info.capacity,
        itemStyle: { color: name === '光伏' ? '#52c41a' : name === '储能' ? '#1890ff' : name === '充电桩' ? '#722ed1' : '#fa8c16' },
      })),
      label: { formatter: '{b}\n{d}%' }
    }]
  } : {};

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!aggregate) return <div>暂无数据</div>;

  // 调节速率/响应时间从资源对象真实读取；无数据字段显示「—」
  const rates = resources.map((r) => r.regulation_rate).filter((v) => v != null);
  const regulationRate = rates.length
    ? parseFloat((rates.reduce((s, v) => s + v, 0) / rates.length).toFixed(1))
    : null;
  const responses = resources.map((r) => r.response).filter((v) => v != null);
  const minResponse = responses.length ? Math.min(...responses) : null;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="聚合总容量" value={aggregate.total_capacity} suffix="kW" valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="有效调节容量" value={aggregate.total_available} suffix="kW" valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="上调能力" value={aggregate.total_regulation_up} suffix="kW" valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="下调能力" value={aggregate.total_regulation_down} suffix="kW" valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={8}><Card title="资源容量分布" size="small"><ReactEChartsCore option={chartOption} style={{ height: 280 }} /></Card></Col>
        <Col span={16}>
          <Card title="关键指标" size="small">
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Card size="small" type="inner" title="调节速率">
                  {regulationRate != null
                    ? <Statistic title="" value={regulationRate} suffix="%/min" precision={1} valueStyle={{ color: '#52c41a', fontSize: 24 }} />
                    : <div style={{ fontSize: 24, color: '#999', padding: '8px 0' }}>—</div>}
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" type="inner" title="调节响应">
                  {minResponse != null
                    ? <Statistic title="" value={minResponse} suffix="ms" valueStyle={{ color: '#52c41a', fontSize: 24 }} />
                    : <div style={{ fontSize: 24, color: '#999', padding: '8px 0' }}>—</div>}
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" type="inner" title="资源数量"><Statistic title="" value={aggregate.total_resources} suffix="个" valueStyle={{ fontSize: 24 }} /></Card>
              </Col>
            </Row>
            <div style={{ marginTop: 16, padding: '12px 16px', background: aggregate.total_capacity >= 10000 ? '#f6ffed' : '#fff7e6', border: `1px solid ${aggregate.total_capacity >= 10000 ? '#b7eb8f' : '#ffd591'}`, borderRadius: 6 }}>
              {aggregate.total_capacity >= 10000
                ? '✅ 满足 GB/T 47241-2026 聚合容量≥10MW 要求'
                : '⚠️ 当前为演示数据，生产环境需达到聚合容量≥10MW'}
            </div>
          </Card>
        </Col>
      </Row>
      <Card title="聚合资源列表" style={{ marginTop: 16 }} size="small">
        <Table columns={columns} dataSource={resources} pagination={false} size="small" />
      </Card>
    </div>
  );
}
